import React, { useState } from "react";
import { Col } from "react-bootstrap";

import Lead from "../../shared/Lead";
import HealthConditions from "./quality-measurements/HealthConditions";
import BloodPressure from "./quality-measurements/BloodPressure";
import Bmi from "./quality-measurements/Bmi";
import Height from "./quality-measurements/Height";
import Weight from "./quality-measurements/Weight";
import PHQ9 from "./quality-measurements/PHQ9";
import StatinTherapy from "./quality-measurements/StatinTherapy";
import HbA1c from "./quality-measurements/HbA1c";
import Triglycerides from "./quality-measurements/Triglycerides";
import FastingGlucose from "./quality-measurements/FastingGlucose";
import WaistMeasurement from "./quality-measurements/WaistMeasurement";
import KidneyFunctionTest from "./quality-measurements/KidneyFunctionTest";
import TotalCholesterol from "./quality-measurements/TotalCholesterol";
import HdlCholesterol from "./quality-measurements/HdlCholesterol";
import LDLCholesterol from "./quality-measurements/LDLCholesterol";
import * as CONST from "../../helpers/Constants";
import ColonCancerScreening from "./quality-measurements/ColonCancerScreening";
import ScheduledAppointmentReminderAlert from "./ScheduledAppointmentReminderAlert";
import { Patient } from "../../helpers/Contexts";
import * as API from "../../api";

const DataEntryNeeded = ({ task }) => {
  const [patientId, setPatientId] = useState(task.patient.uid);
  const providerPatient = React.useMemo(
    () => ({ patientId, setPatientId }),
    [patientId, setPatientId]
  );
  async function FieldHandler(req) {

    task.dispatch({ type: "set_loading", payload: true });
    let patientUid = task.patient.uid;
    let cprId = task.cpr.id;
    let qmtId = req.quality_measurement_type_id;

    let params = {
      patient_id: patientUid.toString(),
      quality_measurement_type_id: qmtId.toString(),
      value: req.value,
      cpr_id: cprId.toString(),
      measured_at_date: req.measured_at_date,
      attending_provider: task.cpr.attending_provider,
    };

    try {
      let res;
      if (req.id) {
        res = await API.updateQualityMeasurement({ quality_measurement: { ...params, id: req.id } });
      } else {
        res = await API.postQualityMeasurement({ quality_measurement: { ...params } });
      }

      // The API layer has already shown the server's error in a popup
      if (res?.error) {
        task.dispatch({ type: "set_loading", payload: false });
        return { error: res.error };
      }

      if (res && req.document) {
        await API.uploadFile(res.id || req.id, req.document)
      }

      task.dispatch({ type: "set_current_task", payload: await API.getTaskById(task.id) });
      return { success: res };

    } catch (err) {
      console.log("Error @ API[updateQualityMeasurement]", err);
      task.dispatch({ type: "set_loading", payload: false });
      return { error: err };
    }
  }

  const PrimaryCodeQualityMeasurements = () =>
    task.cpr.quality_measures_needed.map((i) => {
      return (
        <div key={i.label}>
          {i.label == "Height" && (
            <Height data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "Weight" && (
            <Weight data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "Blood Pressure" && (
            <BloodPressure data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}

          {i.label == "Body Mass Index (BMI)" && (
            <Bmi data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}

          {i.label == "Statin Therapy" && (
            <StatinTherapy data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "PHQ9" && (
            <PHQ9 data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "Waist Measurement" && (
            <WaistMeasurement data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "Fasting Glucose" && (
            <FastingGlucose data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "HDL Cholesterol" && (
            <HdlCholesterol data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "Triglycerides" && (
            <Triglycerides data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
        </div>
      );
    });

  const MapLabQualityMeasurements = () =>
    task.cpr.quality_measures_needed.map((i) => {
      return (
        <div key={i.label}>
          {i.label == "LDL Cholesterol" && (
            <LDLCholesterol data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "Total Cholesterol" && (
            <TotalCholesterol data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "HbA1c" && (
            <HbA1c data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
        </div>
      );
    });

  const MapNonPrimaryQualityMeasurements = () =>
    task.cpr.quality_measures_needed.map((i) => {
      return (
        <div key={i.label}>
          {i.label == "Blood Pressure" && (
            <BloodPressure data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "HbA1c" && (
            <HbA1c data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "Total Cholesterol" && (
            <TotalCholesterol data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.label == "LDL Cholesterol" && (
            <LDLCholesterol data={{ ...i, cprId: task.cpr.id }} handleField={FieldHandler} />
          )}
          {i.value_type == "boolean" && (
            <div className="mt10">
              <b>{i.label}</b> <br />
              <span>Data entry not needed for this quality measure</span>
            </div>
          )}
        </div>
      );
    });

  return (
    <>
      <Col className={"appointment br"} md={12} lg={4} sm={12}>
        <Lead
          message={
            task.task_type == "appointment_request"
              ? "Appointment Data Entry"
              : "Care Plan Requirement(CPR) Data Entry"
          }
        />
        <span id="from">
          <br />
          <br />
          wellPORTAL Patient
        </span>
        <br />
        <b>{task.patient.name} </b>
        <Patient.Provider value={providerPatient}>
          <HealthConditions defaultValue={[...task.patient.health_conditions]} />{" "}
          <label>Quality Measurements</label>
          <span id="from">
            Enter data (obtained from the results document) on all quality measurements that appear
            below
          </span>
          {task.cpr.primary_code && task.cpr.procedure_type !== "Colon Cancer Screening" && (
            <PrimaryCodeQualityMeasurements />
          )}
          {task.cpr.procedure_type == "Lab/Diagnostic Only" && <MapLabQualityMeasurements />}
          {task.cpr.primary_code == false &&
            task.cpr.procedure_type !== "Colon Cancer Screening" && (
              <MapNonPrimaryQualityMeasurements />
            )}
          {!task.cpr.primary_code &&
            [...CONST.COLON_CANCER_SCREENING].includes(task.cpr.procedure_type) && (
              <ColonCancerScreening
                task={{ ...task }}
                handleChange={(e) => console.log("QualityMeasurementUpdated[ColonCancerScreening]")}
                values={!!task.cpr.quality_measurements.length ? task.cpr.quality_measurements : ""}
              />
            )}
            { task.cpr.quality_measures_needed.find((el) => CONST.KIDNEY_FUNCTION_TEST.includes(el.label)) && (
              <KidneyFunctionTest
                task={{ ...task }}
                values={!!task.cpr.quality_measurements.length ? task.cpr.quality_measurements : []}
                data={{ cprId: task.cpr.id }} handleField={FieldHandler}
              />
            )}
          <br />
          <br />
          <b>
            Need to create a new specialist referral? (Wynn only)
            <a
              target="_blank"
              href={"/physicians/tasks/new?patient_id=" + task.patient.uid + "&task_type=Specialist Referral"}
            >
              <i className="ml5 fa fa-plus"></i> Click here
            </a>
          </b>
          <br />
          <br />
          <b>
            Need to enter additional measurements?
            <a
              target="_blank"
              href={"/physicians/patient_info/" + task.patient.uid + "#quality-measurements-header"}
            >
              <i className="ml5 fa fa-plus"></i> Click here
            </a>
          </b>
          <br />
          <br />
          <b>
            Need to upload an order for a care plan requirement (CPR)?
            <a
              target="_blank"
              href={"/physicians/patient_info/" + task.patient.uid + "#patient-cprs-section"}
            >
              <i className="ml5 fa fa-plus"></i> Click here
            </a>
          </b>
          <ScheduledAppointmentReminderAlert />
        </Patient.Provider>
      </Col>
    </>
  );
};

export default DataEntryNeeded;
